import { useEffect, useState } from 'react';
import {
  clientRelations,
  type ClientActivity, type ClientActivityInput, type ClientContact, type ClientContactInput,
  type ClientOptions, type ClientProfile, type ClientProfileInput,
} from '@/api/endpoints';
import { Icon } from './Icon';

/**
 * Karty relacji z klientem na wzór CRM (Firmao): profil firmy, osoby kontaktowe z rolami
 * i historia kontaktów z następnym krokiem. Dane podstawowe (nazwa, NIP, adres) zostają w ClientFormModal.
 */
export function ClientRelationsCards({ clientId, canEdit, onProfileSaved }: {
  clientId: number;
  canEdit: boolean;
  onProfileSaved?: () => void;
}) {
  const [opts, setOpts] = useState<ClientOptions | null>(null);
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [contacts, setContacts] = useState<ClientContact[] | null>(null);
  const [activities, setActivities] = useState<ClientActivity[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [contactEdit, setContactEdit] = useState<ClientContact | 'new' | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const reload = () => {
    setErr(null);
    Promise.all([
      clientRelations.profile(clientId).then(setProfile),
      clientRelations.contacts(clientId).then(setContacts),
      clientRelations.activities(clientId).then(setActivities),
    ]).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => {
    clientRelations.options().then(setOpts).catch(() => {});
  }, []);
  useEffect(() => {
    setProfile(null); setContacts(null); setActivities(null);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (err) return <div className="card mb-4"><div className="card-body text-red-500 text-[13px]">Błąd: {err}</div></div>;

  const visibleContacts = (contacts ?? []).filter(c => showInactive || c.active);
  const inactiveCount = (contacts ?? []).filter(c => !c.active).length;

  return (
    <>
      <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr)' }}>
        {/* ── Profil ─────────────────────────────────────────────────────────── */}
        <div className="card">
          <div className="card-head">
            <div className="card-title">Profil klienta</div>
            {canEdit && profile && (
              <button onClick={() => setProfileOpen(true)} className="ml-auto btn btn-ghost btn-sm">
                <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj
              </button>
            )}
          </div>
          <div className="card-body text-[13px]">
            {!profile && <div className="text-ink-400">Ładowanie…</div>}
            {profile && (
              <dl className="grid gap-x-3 gap-y-1.5" style={{ gridTemplateColumns: 'auto minmax(0,1fr)' }}>
                <Row label="Status">
                  {profile.clientStatusName
                    ? <span className={'pill ' + statusPill(profile.clientStatus)}>{profile.clientStatusName}</span>
                    : '—'}
                </Row>
                <Row label="Typ">{profile.clientTypeName ?? '—'}</Row>
                <Row label="Opiekun">{profile.accountManagerName ?? '—'}</Row>
                <Row label="Skrót">{profile.shortName ?? '—'}</Row>
                <Row label="REGON"><span className="font-mono">{profile.regon ?? '—'}</span></Row>
                <Row label="KRS"><span className="font-mono">{profile.krs ?? '—'}</span></Row>
                <Row label="WWW">
                  {profile.website
                    ? <a href={/^https?:\/\//.test(profile.website) ? profile.website : 'https://' + profile.website}
                         target="_blank" rel="noreferrer" className="text-water-600 hover:underline">{profile.website}</a>
                    : '—'}
                </Row>
                <Row label="Faktury na">
                  {profile.invoiceEmail
                    ? <a href={'mailto:' + profile.invoiceEmail} className="text-water-600 hover:underline">{profile.invoiceEmail}</a>
                    : '—'}
                </Row>
                <Row label="Korespondencja">{profile.correspondenceAddress ?? '—'}</Row>
                <Row label="Rachunek"><span className="font-mono text-[12px]">{formatAccount(profile.bankAccount) ?? '—'}</span></Row>
              </dl>
            )}
          </div>
        </div>

        {/* ── Osoby kontaktowe ───────────────────────────────────────────────── */}
        <div className="card">
          <div className="card-head">
            <div className="card-title">Osoby kontaktowe</div>
            {inactiveCount > 0 && (
              <label className="ml-auto text-[12px] text-ink-500 flex items-center gap-1.5">
                <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}/>
                pokaż nieaktualne ({inactiveCount})
              </label>
            )}
            {canEdit && (
              <button onClick={() => setContactEdit('new')}
                      className={(inactiveCount > 0 ? '' : 'ml-auto ') + 'btn btn-secondary btn-sm'}>
                <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj osobę
              </button>
            )}
          </div>
          <div className="card-body !p-0">
            {contacts == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
            {contacts && visibleContacts.length === 0 && (
              <div className="p-4 text-ink-400 text-[13px]">
                Brak osób kontaktowych. Dodaj np. prezesa (podpisuje umowę), kierownika ujęcia (sprawy techniczne)
                i księgowość (faktury).
              </div>
            )}
            {visibleContacts.length > 0 && (
              <table className="table-hw">
                <thead>
                  <tr><th>Osoba</th><th>Telefon</th><th>E-mail</th><th>Od czego</th><th></th></tr>
                </thead>
                <tbody>
                  {visibleContacts.map(c => (
                    <tr key={c.id} className={c.active ? '' : 'opacity-50'}>
                      <td className="text-[13px]">
                        <div className="text-ink-800 font-medium">
                          {c.fullName}
                          {c.primary && <span className="pill pill-water ml-2">główna</span>}
                          {!c.active && <span className="pill ml-2">nieaktualna</span>}
                        </div>
                        {(c.position || c.department) && (
                          <div className="text-[12px] text-ink-500">{[c.position, c.department].filter(Boolean).join(' · ')}</div>
                        )}
                        {c.notes && <div className="text-[12px] text-ink-500 italic">{c.notes}</div>}
                      </td>
                      <td className="font-mono text-[12px]">
                        {[c.phone, c.phone2].filter(Boolean).map(p => (
                          <div key={p}><a href={'tel:' + p!.replace(/\s/g, '')} className="text-water-600 hover:underline">{p}</a></div>
                        ))}
                        {!c.phone && !c.phone2 && <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-[12px]">
                        {[c.email, c.email2].filter(Boolean).map(m => (
                          <div key={m}><a href={'mailto:' + m} className="text-water-600 hover:underline">{m}</a></div>
                        ))}
                        {!c.email && !c.email2 && <span className="text-ink-400">—</span>}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {c.roles.map(r => <span key={r.code} className="pill pill-earth">{r.label}</span>)}
                        </div>
                      </td>
                      <td className="text-right whitespace-nowrap">
                        {canEdit && (
                          <button onClick={() => setContactEdit(c)} className="btn btn-ghost btn-icon" title="Edytuj">
                            <Icon name="edit" className="w-3.5 h-3.5"/>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <ActivitiesCard clientId={clientId} canEdit={canEdit} opts={opts}
                      contacts={(contacts ?? []).filter(c => c.active)}
                      activities={activities} onChanged={reload}/>

      {profileOpen && profile && (
        <ProfileModal clientId={clientId} profile={profile} opts={opts}
                      onClose={() => setProfileOpen(false)}
                      onSaved={p => { setProfile(p); setProfileOpen(false); onProfileSaved?.(); }}/>
      )}
      {contactEdit && (
        <ContactModal clientId={clientId} contact={contactEdit === 'new' ? null : contactEdit} opts={opts}
                      onClose={() => setContactEdit(null)}
                      onSaved={() => { setContactEdit(null); reload(); }}/>
      )}
    </>
  );
}

// ── Historia kontaktów ─────────────────────────────────────────────────────────────────

const EMPTY_ACTIVITY: ClientActivityInput = {
  kind: 'TELEFON', activityAt: null, contactId: null, summary: '', details: null, nextStep: null, nextStepDate: null,
};

function ActivitiesCard({ clientId, canEdit, opts, contacts, activities, onChanged }: {
  clientId: number;
  canEdit: boolean;
  opts: ClientOptions | null;
  contacts: ClientContact[];
  activities: ClientActivity[] | null;
  onChanged: () => void;
}) {
  const [form, setForm] = useState<ClientActivityInput>(EMPTY_ACTIVITY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const open = (activities ?? []).filter(a => a.nextStep && !a.nextStepDone);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    clientRelations.createActivity(clientId, {
      ...form,
      summary: form.summary.trim(),
      details: form.details?.trim() || null,
      nextStep: form.nextStep?.trim() || null,
      nextStepDate: form.nextStep?.trim() ? form.nextStepDate || null : null,
    })
      .then(() => { setForm({ ...EMPTY_ACTIVITY, kind: form.kind }); onChanged(); })
      .catch(e2 => setErr(e2?.message ?? String(e2)))
      .finally(() => setBusy(false));
  };

  const toggle = (a: ClientActivity) =>
    clientRelations.markDone(clientId, a.id, !a.nextStepDone).then(onChanged).catch(e => setErr(e?.message ?? String(e)));
  const remove = (a: ClientActivity) => {
    if (!confirm('Usunąć wpis „' + a.summary + '”?')) return;
    clientRelations.deleteActivity(clientId, a.id).then(onChanged).catch(e => setErr(e?.message ?? String(e)));
  };

  return (
    <div className="card mb-4">
      <div className="card-head">
        <div className="card-title">Historia kontaktów</div>
        {open.length > 0 && (
          <span className="ml-auto text-[12px] text-ink-500">
            Otwarte kroki: <b className="text-ink-800">{open.length}</b>
            {open.some(a => a.nextStepDate && a.nextStepDate < today) && <span className="text-red-600"> · są po terminie</span>}
          </span>
        )}
      </div>
      <div className="card-body">
        {canEdit && (
          <form onSubmit={submit} className="grid gap-2 mb-4 pb-4 border-b border-ink-100"
                style={{ gridTemplateColumns: '140px 200px minmax(0,1fr)' }}>
            <select className="input" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>
              {(opts?.activityKinds ?? [{ code: 'TELEFON', label: 'Telefon' }]).map(o =>
                <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
            <select className="input" value={form.contactId ?? ''}
                    onChange={e => setForm({ ...form, contactId: e.target.value ? Number(e.target.value) : null })}>
              <option value="">— z kim —</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.fullName}</option>)}
            </select>
            <input className="input" placeholder="Czego dotyczył kontakt, np. „pytał o termin operatu”" value={form.summary}
                   onChange={e => setForm({ ...form, summary: e.target.value })}/>
            <textarea className="input col-span-3" rows={2} placeholder="Szczegóły (opcjonalnie)" value={form.details ?? ''}
                      onChange={e => setForm({ ...form, details: e.target.value })}/>
            <input className="input col-span-2" placeholder="Następny krok, np. „wysłać ofertę” (opcjonalnie)"
                   value={form.nextStep ?? ''} onChange={e => setForm({ ...form, nextStep: e.target.value })}/>
            <div className="flex gap-2">
              <input type="date" className="input" value={form.nextStepDate ?? ''} disabled={!form.nextStep?.trim()}
                     onChange={e => setForm({ ...form, nextStepDate: e.target.value || null })}/>
              <button type="submit" className="btn btn-primary btn-sm whitespace-nowrap" disabled={busy || !form.summary.trim()}>
                <Icon name="plus" className="w-3.5 h-3.5"/> Zapisz
              </button>
            </div>
            {err && <div className="col-span-3 text-[12px] text-red-600">{err}</div>}
          </form>
        )}
        {activities == null && <div className="text-ink-400 text-[13px]">Ładowanie…</div>}
        {activities && activities.length === 0 && (
          <div className="text-ink-400 text-[13px]">Brak wpisów. Zapisuj tu rozmowy, maile i ustalenia z klientem.</div>
        )}
        {activities && activities.length > 0 && (
          <ul className="m-0 p-0 list-none">
            {activities.map(a => {
              const overdue = !!a.nextStep && !a.nextStepDone && !!a.nextStepDate && a.nextStepDate < today;
              const dueToday = !!a.nextStep && !a.nextStepDone && a.nextStepDate === today;
              return (
                <li key={a.id} className="py-2 border-b border-ink-50 last:border-0 text-[13px]">
                  <div className="flex items-baseline gap-2">
                    <span className="pill pill-earth shrink-0">{a.kindName}</span>
                    <span className="font-mono text-[12px] text-ink-500 shrink-0">{fmtDateTime(a.activityAt)}</span>
                    <span className="text-ink-800 min-w-0">
                      {a.summary}
                      {a.contactName && <span className="text-ink-500"> · {a.contactName}</span>}
                    </span>
                    <span className="ml-auto text-[11px] text-ink-400 shrink-0">{a.createdByName}</span>
                    {canEdit && (
                      <button onClick={() => remove(a)} className="btn btn-ghost btn-icon shrink-0" title="Usuń wpis">
                        <Icon name="x" className="w-3 h-3"/>
                      </button>
                    )}
                  </div>
                  {a.details && <div className="text-[12px] text-ink-600 mt-1 whitespace-pre-line pl-1">{a.details}</div>}
                  {a.nextStep && (
                    <label className={'mt-1 flex items-center gap-2 text-[12px] '
                      + (a.nextStepDone ? 'text-ink-400 line-through' : overdue ? 'text-red-600' : dueToday ? 'text-amber-700' : 'text-ink-700')}>
                      <input type="checkbox" checked={a.nextStepDone} disabled={!canEdit} onChange={() => toggle(a)}/>
                      Następny krok: {a.nextStep}
                      {a.nextStepDate && <span className="font-mono">({a.nextStepDate}{overdue ? ', po terminie' : dueToday ? ', dziś' : ''})</span>}
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Modale ─────────────────────────────────────────────────────────────────────────────

function ProfileModal({ clientId, profile, opts, onClose, onSaved }: {
  clientId: number;
  profile: ClientProfile;
  opts: ClientOptions | null;
  onClose: () => void;
  onSaved: (p: ClientProfile) => void;
}) {
  const [d, setD] = useState<ClientProfileInput>({
    shortName: profile.shortName, clientType: profile.clientType, clientStatus: profile.clientStatus,
    regon: profile.regon, krs: profile.krs, website: profile.website, invoiceEmail: profile.invoiceEmail,
    correspondenceAddress: profile.correspondenceAddress, bankAccount: profile.bankAccount,
    accountManagerId: profile.accountManagerId,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const txt = (k: keyof ClientProfileInput) => ({
    value: (d[k] as string | null) ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setD({ ...d, [k]: e.target.value || null }),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    clientRelations.saveProfile(clientId, d).then(onSaved)
      .catch(e2 => setErr(e2?.message ?? String(e2))).finally(() => setBusy(false));
  };

  return (
    <Modal title="Profil klienta" onClose={onClose} onSubmit={submit} busy={busy} err={err}>
      <label className="field">
        <span className="field-label">Status</span>
        <select className="input" value={d.clientStatus ?? ''} onChange={e => setD({ ...d, clientStatus: e.target.value || null })}>
          <option value="">—</option>
          {opts?.statuses.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
      </label>
      <label className="field">
        <span className="field-label">Typ klienta</span>
        <select className="input" value={d.clientType ?? ''} onChange={e => setD({ ...d, clientType: e.target.value || null })}>
          <option value="">—</option>
          {opts?.types.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
      </label>
      <label className="field">
        <span className="field-label">Opiekun klienta</span>
        <select className="input" value={d.accountManagerId ?? ''}
                onChange={e => setD({ ...d, accountManagerId: e.target.value ? Number(e.target.value) : null })}>
          <option value="">—</option>
          {opts?.managers.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
      </label>
      <label className="field">
        <span className="field-label">Nazwa skrócona</span>
        <input className="input" placeholder="np. WZ" {...txt('shortName')}/>
      </label>
      <label className="field">
        <span className="field-label">REGON</span>
        <input className="input font-mono" maxLength={17} {...txt('regon')}/>
      </label>
      <label className="field">
        <span className="field-label">KRS</span>
        <input className="input font-mono" maxLength={12} {...txt('krs')}/>
      </label>
      <label className="field">
        <span className="field-label">Strona WWW</span>
        <input className="input" placeholder="www.…" {...txt('website')}/>
      </label>
      <label className="field">
        <span className="field-label">E-mail do faktur</span>
        <input type="email" className="input" placeholder="faktury@…" {...txt('invoiceEmail')}/>
      </label>
      <label className="field col-span-2">
        <span className="field-label">Adres do korespondencji (gdy inny niż siedziba)</span>
        <input className="input" {...txt('correspondenceAddress')}/>
      </label>
      <label className="field col-span-2">
        <span className="field-label">Numer rachunku</span>
        <input className="input font-mono" placeholder="PL 00 0000 0000 0000 0000 0000 0000" {...txt('bankAccount')}/>
      </label>
    </Modal>
  );
}

function ContactModal({ clientId, contact, opts, onClose, onSaved }: {
  clientId: number;
  contact: ClientContact | null;
  opts: ClientOptions | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [d, setD] = useState<ClientContactInput>({
    firstName: contact?.firstName ?? null, lastName: contact?.lastName ?? '',
    position: contact?.position ?? null, department: contact?.department ?? null,
    phone: contact?.phone ?? null, phone2: contact?.phone2 ?? null,
    email: contact?.email ?? null, email2: contact?.email2 ?? null,
    roles: contact?.roles.map(r => r.code) ?? [],
    primary: contact?.primary ?? false, active: contact?.active ?? true, notes: contact?.notes ?? null,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const txt = (k: keyof ClientContactInput) => ({
    value: (d[k] as string | null) ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setD({ ...d, [k]: e.target.value || null }),
  });
  const toggleRole = (code: string) =>
    setD({ ...d, roles: d.roles.includes(code) ? d.roles.filter(r => r !== code) : [...d.roles, code] });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const op = contact
      ? clientRelations.updateContact(clientId, contact.id, d)
      : clientRelations.createContact(clientId, d);
    op.then(onSaved).catch(e2 => setErr(e2?.message ?? String(e2))).finally(() => setBusy(false));
  };
  const remove = () => {
    if (!contact || !confirm('Usunąć osobę ' + contact.fullName + '? Wpisy w historii kontaktów zostaną.')) return;
    setBusy(true);
    clientRelations.deleteContact(clientId, contact.id).then(onSaved)
      .catch(e2 => setErr(e2?.message ?? String(e2))).finally(() => setBusy(false));
  };

  return (
    <Modal title={contact ? 'Osoba kontaktowa' : 'Nowa osoba kontaktowa'} onClose={onClose} onSubmit={submit}
           busy={busy} err={err}
           extra={contact && <button type="button" onClick={remove} className="btn btn-ghost btn-sm text-red-600 mr-auto">Usuń osobę</button>}>
      <label className="field">
        <span className="field-label">Imię</span>
        <input className="input" autoFocus {...txt('firstName')}/>
      </label>
      <label className="field">
        <span className="field-label">Nazwisko *</span>
        <input className="input" value={d.lastName} onChange={e => setD({ ...d, lastName: e.target.value })}/>
      </label>
      <label className="field">
        <span className="field-label">Stanowisko</span>
        <input className="input" placeholder="np. Prezes Zarządu" {...txt('position')}/>
      </label>
      <label className="field">
        <span className="field-label">Dział</span>
        <input className="input" placeholder="np. Dział techniczny" {...txt('department')}/>
      </label>
      <label className="field">
        <span className="field-label">Telefon</span>
        <input className="input font-mono" {...txt('phone')}/>
      </label>
      <label className="field">
        <span className="field-label">Telefon 2</span>
        <input className="input font-mono" {...txt('phone2')}/>
      </label>
      <label className="field">
        <span className="field-label">E-mail</span>
        <input type="email" className="input" {...txt('email')}/>
      </label>
      <label className="field">
        <span className="field-label">E-mail 2</span>
        <input type="email" className="input" {...txt('email2')}/>
      </label>
      <div className="field col-span-2">
        <span className="field-label">Od czego jest ta osoba</span>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px]">
          {opts?.contactRoles.map(o => (
            <label key={o.code} className="flex items-center gap-1.5">
              <input type="checkbox" checked={d.roles.includes(o.code)} onChange={() => toggleRole(o.code)}/>
              {o.label}
            </label>
          ))}
        </div>
      </div>
      <div className="col-span-2 flex gap-6 text-[13px]">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={d.primary} disabled={!d.active}
                 onChange={e => setD({ ...d, primary: e.target.checked })}/>
          Główna osoba kontaktowa
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={!d.active}
                 onChange={e => setD({ ...d, active: !e.target.checked, primary: e.target.checked ? false : d.primary })}/>
          Już tu nie pracuje (nieaktualna)
        </label>
      </div>
      <label className="field col-span-2">
        <span className="field-label">Notatki</span>
        <textarea className="input" rows={2} value={d.notes ?? ''}
                  onChange={e => setD({ ...d, notes: e.target.value || null })}/>
      </label>
    </Modal>
  );
}

function Modal({ title, onClose, onSubmit, busy, err, extra, children }: {
  title: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  busy: boolean;
  err: string | null;
  extra?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={onSubmit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[640px] max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-100">
          <h2 className="text-[16px] font-semibold text-ink-900">{title}</h2>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-icon text-ink-400 hover:text-ink-700">
            <Icon name="x"/>
          </button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">{children}</div>
        {err && <div className="px-5 pb-2 text-[12px] text-red-600">{err}</div>}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-ink-100">
          {extra}
          <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Anuluj</button>
          <button type="submit" disabled={busy} className="btn btn-primary btn-sm">{busy ? 'Zapisywanie…' : 'Zapisz'}</button>
        </div>
      </form>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-ink-500 text-[12px]">{label}</dt>
      <dd className="m-0 text-ink-800 min-w-0 break-words">{children}</dd>
    </>
  );
}

function statusPill(code: string | null) {
  return code === 'AKTYWNY' ? 'pill-green' : code === 'POTENCJALNY' ? 'pill-amber' : '';
}

function formatAccount(nr: string | null) {
  if (!nr) return null;
  const pl = nr.startsWith('PL') ? 'PL ' : '';
  const digits = nr.replace(/^PL/, '');
  return pl + digits.slice(0, 2) + ' ' + (digits.slice(2).match(/.{1,4}/g) ?? []).join(' ');
}

function fmtDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('pl-PL') + ' ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
}
